/**
 * The local transaction engine: author, fold, flush.
 *
 * Three steps, in this order, with no network involved (§6.1):
 *
 *   1. append `SyncEnvelope` to the durable outbox   ← the durability point (I6)
 *   2. fold(pending) → re-render                     ← zero-latency UI
 *   3. schedule a flush                              ← network, never awaited
 *
 * The UI never awaits the network; it awaits only the local IndexedDB write,
 * which is what makes offline capture feel instant. Step 1 is what a crash
 * after it costs nothing.
 */

import {
  ANONYMOUS_OWNER_ID,
  appendToOutbox,
  committedOps,
  currentDbGeneration,
  currentDbOwner,
  db,
  discardOutbox,
  getMeta,
  markCommitted,
  markRejected,
  mergeSession,
  pendingCreateNotebooks,
  pendingOps,
  peekOutbox,
  readSession,
  setDbOwner,
  setMeta,
  storeCommitted,
  writeSession,
  type OpRow,
} from './db';
import {
  derivePageIds,
  fold,
  foldFromSnapshot,
  foldPending,
  type FoldContext,
  type RelayoutEntry,
} from './fold';
import { uuidv7 } from './identity';
import type {
  AutofocusSession,
  CommittedEnvelope,
  LogPage,
  Op,
  Projection,
  RenderModel,
  SyncEnvelope,
} from '../types/generated';

/** How many page ids to precompute. Deep enough for any realistic session. */
const PAGE_WINDOW = 256;

/** The first-run identity, minted once and persisted (ARCHITECTURE.md §3). */
export interface Identity {
  owner_id: string;
  device_id: string;
  notebook_id: string;
}

/**
 * Adopts an existing identity instead of minting one.
 *
 * A second device on the same notebook has a different `device_id` but the same
 * `notebook_id` and `owner_id`. Tests need this to model two devices; a real
 * second device gets it by pairing (§10).
 */
export async function storeFor(identity: Identity, pollMs?: number): Promise<NotebookStore> {
  const store = new NotebookStore(pollMs);
  await store.open(identity);
  return store;
}

/**
 * The synthetic owner (ARCHITECTURE.md §3): the one unclaimed, pre-account
 * owner. Every notebook minted before anyone signs in is authored by this id —
 * the sentinel means "no account yet", not "one person, several notebooks".
 *
 * Defined in `db.ts` (it is the default of the browser's acting owner, which is
 * what names the local store) and re-exported here, where the engine and the UI
 * have always read it.
 */
export { ANONYMOUS_OWNER_ID };

/**
 * Reads or mints the per-browser `device_id` and persists it.
 *
 * A reload MUST reuse the stored id rather than re-creating it: the outbox
 * attributes rows to it and the session cell uses it as the LWW tie-break
 * (§5.2).
 *
 * It lives in `localStorage`, NOT in the owner-scoped database (design §5): it
 * is device identity, so two accounts signing in on one browser are one device.
 * A per-owner copy would fork it and hand the session merge two ids for one
 * screen. The `meta` rows are read as a fallback for a browser that stored the
 * id before this moved (the pre-Step-7 `identity` row, then `device_id`).
 */
export async function getOrCreateDeviceId(): Promise<string> {
  const stored = readStoredDeviceId();
  if (stored) return stored;

  const legacy = await getMeta<{ device_id?: string }>('identity');
  if (legacy?.device_id) return adoptDeviceId(legacy.device_id);
  const metaStored = await getMeta<string>('device_id');
  if (metaStored) return adoptDeviceId(metaStored);

  return adoptDeviceId(uuidv7());
}

/** The `localStorage` key the per-browser device id lives under. */
const DEVICE_ID_KEY = 'af_device_id';

/** The stored device id, or `null` when there is no usable storage. */
function readStoredDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Writes `deviceId` where the browser will find it again and returns it.
 *
 * When `localStorage` is unusable (private mode, no DOM), the id falls back to
 * the `meta` row so it is still stable across a reload — worse, since it forks
 * with the owner, but better than a new device identity on every call.
 */
async function adoptDeviceId(deviceId: string): Promise<string> {
  let persisted = true;
  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  } catch {
    persisted = false;
  }
  if (!persisted) await setMeta('device_id', deviceId);
  return deviceId;
}

/** A notebook being created — the modal's commit shape (STEP_7_SPEC.md D6). */
export interface NotebookDraft {
  name: string;
  tag: string;
  description: string;
  pageCapacity: number;
  /** Optional; defaults to the browser's zone (§7.2a). */
  timezone?: string;
  /** The captured text, when creation came from an unknown-tag capture (§D4). */
  firstTask?: string;
}

/**
 * Creates a notebook: mints its id, authors `CreateNotebook` — then
 * `AppendTask` when a first task was captured — into ONE outbox, and returns
 * the new store.
 *
 * Both ops live in one outbox drained in ordinal order (A11), so the relay
 * commits the create before the append and `fold_pending` sees the notebook
 * exist before the append renders as page 1, line 0 — no new protocol (§D6).
 *
 * `relay` installs the transport IMMEDIATELY (Step 8 e2e): `authorise`
 * schedules a flush before the caller could set one, and a flush that fires
 * first fails silently against the fallback — stranding the create until
 * some later op happens to re-arm the drain.
 *
 * The owner is whoever this browser is acting as (`currentDbOwner`, design §5):
 * a signed-in user's notebook — and every envelope stamped from this store's
 * identity — must carry THEIR id, or the relay's gate refuses it as cross-owner.
 */
export async function createNotebook(draft: NotebookDraft, relay?: Relay, pollMs?: number): Promise<NotebookStore> {
  const store = await storeFor({
    owner_id: currentDbOwner(),
    device_id: await getOrCreateDeviceId(),
    notebook_id: uuidv7(),
  }, pollMs);
  if (relay) store.setTransport(relay);
  await store.authorise({
    type: 'CreateNotebook',
    name: draft.name,
    tag: draft.tag,
    description: draft.description,
    page_capacity: draft.pageCapacity,
    timezone: draft.timezone ?? browserTimezone(),
  });
  if (draft.firstTask) await store.appendTask(draft.firstTask, null);
  return store;
}

/**
 * One `RenameNotebook` for all three text fields (§D3): a rename is one
 * intent. `undefined` leaves a field unchanged; the relay re-checks
 * uniqueness for a supplied tag.
 */
export async function renameNotebook(
  store: NotebookStore,
  draft: { name: string; tag?: string; description?: string },
): Promise<void> {
  await store.authorise({
    type: 'RenameNotebook',
    name: draft.name,
    tag: draft.tag ?? null,
    description: draft.description ?? null,
  });
}

/** The relay shape of one notebook row — `GET .../notebooks` widened (§D9). */
export interface NotebookSummary {
  notebook_id: string;
  name: string;
  tag: string;
  description: string;
  page_capacity: number;
  head_seq: number;
  /** ms since epoch (§8.1). */
  created_at: number;
}

/**
 * The notebook list, relay-refreshed with a local fallback (§D7).
 *
 * With a relay it asks the transport; without one it fetches the owner route
 * directly. Success caches the payload in `meta.notebookList`; failure (offline)
 * serves the cache PLUS any notebook this device has authored but never
 * refreshed — the outbox knows.
 */
export async function listNotebooks(relay?: Relay): Promise<{
  notebooks: NotebookSummary[];
  fromCache: boolean;
}> {
  try {
    const body = relay
      ? await relay.listNotebooks()
      : await fetchNotebookList();
    await setMeta('notebookList', body.notebooks);
    // The outbox knows: a notebook this device authored but never refreshed is
    // real state regardless of reachability — online, the relay learns about
    // it only when the flush lands.
    const known = new Set(body.notebooks.map((n) => n.notebook_id));
    const pending = (await pendingCreateNotebooks()).filter(
      (p) => !known.has(p.notebook_id),
    );
    return {
      notebooks: [
        ...body.notebooks,
        ...pending.map((p) => ({
          notebook_id: p.notebook_id,
          name: p.name,
          tag: p.tag,
          description: p.description ?? '',
          page_capacity: p.page_capacity,
          head_seq: 0,
          created_at: 0,
        })),
      ],
      fromCache: false,
    };
  } catch {
    const cached = (await getMeta<NotebookSummary[]>('notebookList')) ?? [];
    const known = new Set(cached.map((n) => n.notebook_id));
    const pending = (await pendingCreateNotebooks()).filter(
      (p) => !known.has(p.notebook_id),
    );
    return {
      notebooks: [
        ...cached,
        ...pending.map((p) => ({
          notebook_id: p.notebook_id,
          name: p.name,
          tag: p.tag,
          description: p.description ?? '',
          page_capacity: p.page_capacity,
          head_seq: 0,
          created_at: 0,
        })),
      ],
      fromCache: true,
    };
  }
}

/**
 * The owner-scoped list route over `fetch` (§D9).
 *
 * The owner is the one this browser is acting as — the same value that named the
 * database the cached fallback was read from, so the two can never disagree
 * about whose list this is (design §5).
 */
async function fetchNotebookList(): Promise<{ notebooks: NotebookSummary[] }> {
  return fetchOwnerNotebooks(currentDbOwner());
}

/**
 * The same route for ONE named owner, whoever this browser is acting as.
 *
 * The boot gate needs it: it reads the NEW owner's list before the browser acts
 * as them (the adoption in `account.ts`, R18), and at that instant
 * `currentDbOwner()` is still the owner being left. The list is not cached here —
 * the cache belongs to the store the boot is about to open, and the container's
 * own refresh is what writes it.
 */
export async function fetchOwnerNotebooks(
  ownerId: string,
): Promise<{ notebooks: NotebookSummary[] }> {
  const response = await fetch(`/api/owners/${ownerId}/notebooks`);
  if (!response.ok) throw new Error(`notebooks: ${response.status}`);
  return (await response.json()) as { notebooks: NotebookSummary[] };
}

/** The relay's per-notebook import verdict (REPLICATION.md §17.1). */
export interface ImportedNotebook {
  notebook_id: string;
  head_seq: number;
  ops_appended: number;
}

/**
 * POSTs an `af1-export` bundle to the relay's import surface (§17.5).
 *
 * A bulk relay write, not an outbox op: it appends whole notebooks that this
 * device never touched, so nothing is authored locally — the caller refreshes
 * the list to read the relay's truth. The relay is all-or-nothing per file;
 * its error message names the notebook.
 */
export async function importNotebooks(bundleJson: string): Promise<{ imported: ImportedNotebook[] }> {
  const response = await fetch(`/api/owners/${currentDbOwner()}/import`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: bundleJson,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `import: ${response.status}`);
  }
  return (await response.json()) as { imported: ImportedNotebook[] };
}

/** The device-local "open this notebook by default" pointer (§D2, §D7). */
export async function setLastNotebookId(notebookId: string): Promise<void> {
  await setMeta('lastNotebookId', notebookId);
}
export async function getLastNotebookId(): Promise<string | null> {
  return (await getMeta<string>('lastNotebookId')) ?? null;
}

/** Listeners for render-relevant changes. */
type Listener = () => void;

/**
 * The client store.
 *
 * Deliberately a plain class rather than a reactive `$state` object: the Svelte
 * layer subscribes and re-reads, so the storage engine has no compile-time
 * dependency on the UI and can be tested under Node.
 */
export class NotebookStore {
  #identity: Identity | null = null;
  #ctx: FoldContext | null = null;
  #pageIds: Map<number, string> | null = null;
  /** A bootstrap base from `GET /snapshot`, when the log has been pruned. */
  #snapshot: Projection | null = null;
  #listeners = new Set<Listener>();
  #model: RenderModel;
  #session: AutofocusSession | null = null;
  #flushing = false;
  #online = true;
  #transport: Relay | null = null;
  #offline = false;
  /**
   * R5.15: the sync poll. Started once per store; every tick runs `catchUp`,
   * which both proves the relay (lowering/raising the flag through
   * `#goOffline`/`#goOnline`) and pulls whatever another browser authored.
   * No-op ticks stay quiet by `catchUp`'s early return.
   */
  #pollTimer: ReturnType<typeof setTimeout> | null = null;
  #startPolling(): void {
    // Visibility pause/restart can re-enter; one chain only.
    if (this.#pollTimer) return;
    this.#pollTimer = setTimeout(() => {
      this.#pollTimer = null;
      // The chain always re-arms; only the WORK is guarded (the first tick
      // can fire before the identity binds — skipping it must not end the
      // poll).
      if (!this.#stale() && this.#identity) {
        void this.catchUp().catch(() => {});
      }
      this.#startPolling();
    }, this.#pollMs);
  }

  /** Stops the poll — a hidden tab cannot show the sync and browsers throttle
   * its timers into garbage anyway. The flag's transitions are untouched. */
  #pausePolling(): void {
    if (this.#pollTimer) {
      clearTimeout(this.#pollTimer);
      this.#pollTimer = null;
    }
  }

  /** One guarded `catchUp` — the tick's work, or the tab-return nudge. */
  #syncNow(): void {
    if (!this.#stale() && this.#identity) {
      void this.catchUp().catch(() => {});
    }
  }
  /** The DB generation this store opened against; a stale store must not write. */
  #dbGeneration = -1;
  /** Ops the engine refused locally: dropped from the prediction (§5.6). */
  #locallyRejected: Array<{ op_id: string; reason: string }> = [];
  /**
   * The relay refused the drain with `401`: the login is missing or expired.
   *
   * Distinct from every other failure because it is TEMPORARY and expected in
   * account mode — the user signs in again and the same outbox goes up. The
   * pending ops are therefore kept (see the `flush` catch) rather than
   * discarded, and this flag is what the "sign in again" surface hangs on.
   */
  #authRequired = false;
  /** The sync poll's cadence for THIS store (`SYNC_POLL_MS` unless injected). */
  #pollMs: number;

  constructor(pollMs: number = SYNC_POLL_MS) {
    this.#pollMs = pollMs;
    // R5.15: the browser's own network transitions are the one connectivity
    // signal the store can know without a round trip. `offline` lowers the
    // flag outright; `online` raises it and lets the next drain prove it.
    window.addEventListener('offline', () => this.#goOffline());
    window.addEventListener('online', () => this.#goOnline());
    // R5.15 amendment: a hidden tab PAUSES the poll (throttled timers, battery)
    // and a returning tab syncs immediately — the nudge IS the restart.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#pausePolling();
      } else {
        this.#syncNow();
        this.#startPolling();
      }
    });
    // The sync poll runs unconditionally — probe while down, sync while up.
    if (!document.hidden) this.#startPolling();
    this.#model = {
      committed: emptyProjectionSafe(),
      predicted: emptyProjectionSafe(),
    };
  }

  // ── lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Opens an existing identity: a second device on an already-created notebook.
   *
   * Never authors `CreateNotebook` — the notebook exists, and §4.4 permits
   * exactly one per notebook.
   */
  async open(identity: Identity): Promise<Identity> {
    this.#identity = identity;
    this.#dbGeneration = currentDbGeneration();
    await this.#ensureContext();
    await this.refresh();
    return identity;
  }

  get identity(): Identity {
    if (!this.#identity) throw new Error('store not bootstrapped');
    return this.#identity;
  }

  /**
   * Points this store at a new identity, carrying the queued work that is still
   * this person's across the database boundary the change creates (design §5).
   *
   * `owner_id` is baked into every envelope at `authorise`, so an identity change
   * — signing in, or a first-visit offline browser reconciling with the account
   * it turns out to have — leaves the outbox stamped with the owner the browser
   * was acting as until now. The relay's gate refuses a foreign owner's op, and
   * a refused op is a frozen verdict (I5): without this the first signup would
   * reject every queued anonymous op as MALFORMED_ENVELOPE and the user's
   * offline work would silently stop existing (§4).
   *
   * The re-stamp is not enough on its own, because the cache is per-owner too:
   * the identity change IS the database boundary. The queued rows therefore MOVE
   * — read from the store the browser was acting as, re-addressed, then written
   * into the new owner's store once `setDbOwner` has moved the read boundary —
   * or the re-stamped work sits in a database nothing reads any more and never
   * syncs. See `#moveOutboxTo`.
   *
   * Pending rows for a notebook the new owner does not have are NOT carried
   * across: that work belonged to whoever was signed in before, and a shared
   * browser must not transmit one person's notebooks under another person's
   * account. `knownNotebookIds` is authoritative when supplied — an empty set
   * carries nothing — and an ABSENT list carries everything, because an unknown
   * list must never destroy offline work (I6). Committed rows are history and
   * are never rewritten: the drop rule is about the outbox.
   *
   * The identity change is in place: `owner_id` and `notebook_id` both change, so
   * the fold context, the derived page ids and any snapshot are rebuilt from
   * scratch rather than reused for the new person's notebook.
   */
  async setIdentity(identity: Identity, knownNotebookIds?: Set<string>): Promise<void> {
    this.#identity = identity;
    if (!this.#stale()) {
      await this.#moveOutboxTo(identity.owner_id, knownNotebookIds);
      // The move switched the DB owner, which is a new database generation. This
      // store opened against the new owner's database, so it is current again:
      // without this its flushes and session writes would be inert.
      this.#dbGeneration = currentDbGeneration();
    }
    this.#ctx = null;
    this.#pageIds = null;
    this.#snapshot = null;
    this.#session = null;
    await this.#ensureContext();
    await this.refresh();
  }

  /**
   * Moves the queued outbox rows across the database boundary an identity change
   * creates, re-addressed to `ownerId`.
   *
   * The ORDER is the whole point. The pending rows are read from the store this
   * browser has been acting as; then `setDbOwner` moves the read boundary, so
   * `db()` answers for the new owner; then the survivors are written into the new
   * owner's store. Re-stamping in place — which is what this used to do — pointed
   * the envelopes at the new owner while leaving them in the database nothing
   * reads any more: the queue read empty, the relay was never asked to commit the
   * ops, and the offline work was stranded rather than synced (Review Focus 3).
   *
   * Nothing is DELETED, on either side of the boundary. The rows that are not
   * carried across stay in the previous owner's store, which per-owner scoping no
   * longer reads while this identity is in force — and no offline work is
   * destroyed before its replacement is durable (I6). Deleting them would be the
   * only alternative, and it cannot be made atomic with the write into the other
   * database; a browser that acts as the previous owner again reads its own queue,
   * where `op_id` makes a re-sent op a DUPLICATE rather than a second write (I5).
   *
   * The other side of that coin is that a re-carried row must not overwrite what
   * the new owner has already settled: a row it has committed or had refused
   * stays as it is (see the guard below).
   */
  async #moveOutboxTo(ownerId: string, knownNotebookIds?: Set<string>): Promise<void> {
    const queued = await this.#pendingOutboxRows();
    const carried = queued
      .filter(
        // A supplied list is authoritative, empty set included; an absent one is
        // not an empty one — the caller has not loaded the new owner's notebooks
        // and cannot know which of these are theirs, so everything is kept.
        (row) => knownNotebookIds === undefined || knownNotebookIds.has(row.notebook_id),
      )
      .map((row) => restampRowOwner(row, ownerId));

    // The boundary. Everything after this reads and writes the new owner's store.
    await setDbOwner(ownerId);
    if (carried.length === 0) return;

    const database = await db();
    const tx = database.transaction('opLog', 'readwrite');
    for (const row of carried) {
      // A row the new owner has already SETTLED — committed (`seq` set) or frozen
      // by a verdict (I5, A17) — is history, and a carried copy of the same
      // `op_id` must never overwrite it. The move is copy-not-delete, so the
      // source rows survive in the store the browser left; a later identity
      // change that carries them again (the boot gate adopts on every sign-in
      // that has no cached account) would otherwise resurrect an op the relay has
      // already acknowledged, re-send it, and fold it as though it were still
      // pending — a duplicate the `op_id` dedupe would eventually absorb, at the
      // cost of a phantom row in the UI in the meantime.
      const settled = await tx.store.get(row.op_id);
      if (settled && settled.seq !== null) continue;
      await tx.store.put(row);
    }
    await tx.done;
  }

  /**
   * Every unacknowledged row in the CURRENT owner's outbox, in ordinal order.
   *
   * Whole-store rather than per-notebook: an identity change has to account for
   * every notebook this browser has queued work for, not just the one the store
   * happens to be viewing. The `byOrdinal` index cursor already yields the rows
   * in `localOrdinal` order, which is the order they will be sent in (A11).
   */
  async #pendingOutboxRows(): Promise<OpRow[]> {
    const database = await db();
    const rows: OpRow[] = [];
    let cursor = await database
      .transaction('opLog')
      .store.index('byOrdinal')
      .openCursor();
    while (cursor) {
      if (cursor.value.seq === null) rows.push(cursor.value);
      cursor = await cursor.continue();
    }
    return rows;
  }

  /** The two halves of the render model (§4.2.4). */
  get model(): RenderModel {
    return this.#model;
  }

  get session(): AutofocusSession | null {
    return this.#session;
  }

  get online(): boolean {
    return this.#online;
  }

  // ── R5.15: the offline flag's transitions ──────────────────────────────────

  /**
   * Lowers the flag. Every site that learns the relay is unreachable routes
   * through here: a failed flush, a failed catch-up (poll tick included), the
   * browser's own `offline` event. Recovery needs no arming — the sync poll
   * runs unconditionally.
   */
  #goOffline(): void {
    if (!this.#online) return;
    this.#online = false;
    this.#emit();
  }

  /**
   * Raises the flag. Every site that PROVES the relay is reachable routes
   * through here: a drain that answered, a successful catch-up, the browser's
   * own `online` event.
   */
  #goOnline(): void {
    if (this.#online) return;
    this.#online = true;
    this.#emit();
  }

  /**
   * Installs the transport the automatic flush uses.
   *
   * The store authors locally and never blocks on the network, so it needs to
   * know *how* to reach the relay without being handed one per call — otherwise
   * every `authorise` would fall back to `fetch`, which is right in the browser
   * and wrong in a test.
   */
  setTransport(relay: Relay): void {
    this.#transport = relay;
  }

  /**
   * The transport the debug-log view reads the relay through.
   *
   * Falls back to the fetch relay when none was installed — the production
   * shape, where nothing ever calls `setTransport` (the container's `relay`
   * prop is test injection). Without this fallback the debug log rendered
   * blank: its mount guard is truthiness on this getter, and every other
   * relay use in the store already falls back to `defaultRelay`.
   */
  get transport(): Relay {
    return this.#transport ?? defaultRelay(this.identity);
  }

  /**
   * Suppresses automatic flushes until re-enabled.
   *
   * REPLICATION.md §5.6 is blunt about this: "the network being down is the
   * default state of a local-first client, not an edge case." Authoring must
   * therefore work with no transport at all, which is a state worth modelling
   * explicitly rather than leaving to an absent relay's error handling — an
   * absent relay otherwise falls back to `fetch`, which reaches for the network
   * on a timer nobody is waiting for.
   */
  setOffline(offline: boolean): void {
    this.#offline = offline;
    if (!offline) this.scheduleFlush();
  }

  get offline(): boolean {
    return this.#offline;
  }

  /** Ops the local engine refused; surfaced with the same notice as a rejection. */
  get locallyRejected(): ReadonlyArray<{ op_id: string; reason: string }> {
    return this.#locallyRejected;
  }

  /**
   * The relay answered `401`: the browser's login is missing or expired.
   *
   * Set by `flush`, which pauses rather than discards in that case — see the
   * contrast with `RelayGoneError` there — and by `catchUp`, which runs BEFORE
   * the flush on every notebook open (§8.2) and would otherwise hide an expired
   * login behind its generic "unreachable" branch.
   *
   * It is cleared by a drain in which the relay ANSWERED something without a
   * `401` (task 8, R17): signing in again is supposed to make the prompt go
   * away, and a flag that is only ever set invites a UI that never recovers.
   * "Reached the end of the loop" is deliberately not enough — a drain with
   * nothing pending sends nothing and knows nothing about the login. Nothing
   * here swaps the identity — the boot gate (Task 8) builds the store afresh
   * after a sign-in, which is the same path that clears the flag by draining
   * successfully. The live prompt hangs on it (R19, `NotebookContainer`).
   */
  get authRequired(): boolean {
    return this.#authRequired;
  }

  /**
   * Takes the accumulated rejections and clears them.
   *
   * `locallyRejected` is a growing list, and a notice bound to it directly would
   * be re-rendered on every emit — so once per rejection, not once per emit.
   * Draining is what makes "render it once" expressible.
   */
  drainRejections(): Array<{ op_id: string; reason: string }> {
    const drained = this.#locallyRejected;
    this.#locallyRejected = [];
    return drained;
  }

  /**
   * Subscribes to render-relevant changes.
   *
   * Svelte's `$derived` cannot track a plain getter, so a component reading
   * `store.model` would compute once and never recompute — the view would
   * silently stop reflecting the store. Subscribers must therefore bump a
   * reactive counter; `subscribeVersion` does that in one line.
   */
  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Called on every change. Increment a `$state` counter in the caller. */
  get version(): number {
    return this.#version;
  }

  #version = 0;

  #emit(): void {
    this.#version += 1;
    for (const listener of this.#listeners) listener();
  }

  // ── authoring (step 1 + 2, never the network) ──────────────────────────────

  /**
   * Authors one operation: durable locally, folded immediately, flush scheduled.
   *
   * `task_id` is minted HERE, client-side, which is what lets the user finish or
   * re-enter a task before the server has ever acknowledged it (I3).
   */
  async authorise(op: Op): Promise<SyncEnvelope> {
    const identity = this.identity;
    const committed = await committedOps(identity.notebook_id);
    const base_seq = committed.length > 0 ? committed[committed.length - 1]!.seq : 0;

    const envelope: SyncEnvelope = {
      op_id: uuidv7(),
      owner_id: identity.owner_id,
      device_id: identity.device_id,
      notebook_id: identity.notebook_id,
      base_seq,
      client_time: Date.now(),
      logical_day_hint: null,
      op,
    };

    await appendToOutbox(envelope, identity.device_id);
    await this.refresh();
    this.scheduleFlush();
    return envelope;
  }

  /** Appends a task, minting its client-side identity. */
  async appendTask(title: string, parentTaskId: string | null = null): Promise<string> {
    const taskId = uuidv7();
    await this.authorise({ type: 'AppendTask', task_id: taskId, title, parent_task_id: parentTaskId });
    return taskId;
  }

  async finishTask(taskId: string): Promise<void> {
    await this.authorise({ type: 'FinishTask', task_id: taskId });
    await this.bumpItemsWorked();
  }

  /** Branching re-entry: the parent is struck, children append at the tail. */
  async reenterTask(taskId: string, titles: string[]): Promise<void> {
    await this.authorise({
      type: 'ReenterTask',
      task_id: taskId,
      children: titles.map((title) => ({ task_id: uuidv7(), title })),
    });
    await this.bumpItemsWorked();
  }

  async dismissTask(taskId: string): Promise<void> {
    await this.authorise({ type: 'DismissTask', task_id: taskId });
  }

  async dismissPage(pageId: string): Promise<void> {
    await this.authorise({ type: 'DismissPage', page_id: pageId });
  }

  /** Forward compensation, never an inverse (§4.1 / §6.4). */
  async restoreTask(taskId: string): Promise<void> {
    await this.authorise({ type: 'RestoreTask', task_id: taskId });
  }

  async rewordTask(taskId: string, title: string): Promise<void> {
    await this.authorise({ type: 'RewordTask', task_id: taskId, title });
  }

  // ── the outbox drain (A11) ─────────────────────────────────────────────────

  /**
   * True when the database this store opened against has been deleted and
   * replaced. Every durable write from a stale store is skipped: its identity
   * is the shared constant, so without this a store from a previous test (or a
   * torn-down page) keeps draining its timers and session publishes into the
   * CURRENT database — the outbox filter no longer separates stores now that
   * `notebook_id` is shared.
   */
  #stale(): boolean {
    return currentDbGeneration() !== this.#dbGeneration;
  }

  /**
   * Drains the outbox, strictly in local-ordinal order, one batch at a time.
   *
   * Authoring is never blocked by this (A11): the UI path above awaits only the
   * IndexedDB write. What serialising the *drain* buys is that out-of-order and
   * partially-applied batches are impossible by construction, which retires a
   * whole class of bug.
   */
  async flush(relay?: Relay): Promise<FlushReport> {
    if (this.#flushing || this.#offline) {
      return { sent: 0, committed: 0, rejected: 0 };
    }
    if (this.#stale()) {
      return { sent: 0, committed: 0, rejected: 0 };
    }
    relay = relay ?? this.#transport ?? defaultRelay(this.identity);

    this.#flushing = true;
    let sent = 0;
    let committed = 0;
    let rejected = 0;
    /**
     * Whether the relay answered anything in this drain WITHOUT a `401`.
     *
     * This, and not "the loop reached the end", is what proves the login works
     * again (R17). `openNotebook` fires `catchUp` and then an unconditional flush
     * (§8.2), so a notebook with nothing pending runs a drain whose first
     * `peekOutbox` is already empty: it sent nothing and proved nothing, and
     * clearing the flag on it would cancel the `catchUp` 401 one no-op flush
     * later, with no re-authentication in between — the prompt gone, the outbox
     * still stalled, which is the one thing the flag exists to prevent.
     *
     * A `410` counts (the relay answered, and it is not an auth refusal); a `401`
     * cannot, because that branch returns before the loop's end.
     */
    let relayAnswered = false;

    try {
      for (;;) {
        const batch = await peekOutbox(this.identity.notebook_id, BATCH_SIZE);
        if (batch.length === 0) break;

        // One batch at a time; op N+1 is never transmitted until op N's result
        // is known.
        let response: BatchResponse;
        try {
          response = await relay.postOps(batch.map((r) => r.envelope));
        } catch (e) {
          if (!(e instanceof RelayGoneError)) throw e;
          // 410: the notebook was deleted elsewhere. The outbox rows for it
          // can never commit — discard them with one frozen notice, then keep
          // draining (the next peek for this id is empty).
          relayAnswered = true;
          await discardOutbox(this.identity.notebook_id);
          this.#locallyRejected.push({
            op_id: batch[0]!.op_id,
            reason: 'NOTEBOOK_DELETED',
          });
          continue;
        }
        // The batch came back: the login is good for at least this request.
        relayAnswered = true;
        if ('resync_required' in response) {
          // §9.3: the client is too far behind to replay incrementally. Take
          // the snapshot, seed it as our committed prefix, and re-stamp the
          // outbox's `base_seq` onto the new head.
          //
          // The re-stamp is what makes progress: `base_seq` is advisory, but
          // leaving it stale would earn another `resync_required` on the next
          // attempt and spin forever.
          const projection = await relay.fetchSnapshot();
          if (!projection) break;
          await this.seedFromSnapshot(projection);
          await this.restampOutboxBaseSeq(projection.head_seq);
          continue;
        }

        for (let i = 0; i < response.results.length; i += 1) {
          const verdict = response.results[i]!;
          sent += 1;

          if (verdict.status === 'COMMITTED' || verdict.status === 'DUPLICATE') {
            committed += 1;
            // The server's record is authoritative; store it verbatim so a
            // later fold reproduces the server's projection exactly.
            const seq = verdict.seq as number;
            const committed_ = await this.#committedFor(relay, response, verdict, batch[i]!);
            if (committed_) await markCommitted(batch[i]!.op_id, seq, committed_);
          } else {
            // REJECTED and MALFORMED both leave the outbox: a frozen verdict can
            // never later succeed (I5, A17).
            //
            // The verdict's `reason` is recorded rather than discarded. Without
            // it, acting on a task another device already settled produced
            // complete silence — the op left the outbox, the task never changed,
            // and the user's intent silently stopped existing (§4.1).
            //
            // Step 7 lifted the old CreateNotebook suppression: a same-tag race
            // is a real rejection the user must see (Review Focus #4).
            rejected += 1;
            this.#locallyRejected.push({
              op_id: batch[i]!.op_id,
              reason: String(verdict.reason ?? 'MALFORMED_ENVELOPE'),
            });
            await markRejected(batch[i]!.op_id);
          }
        }
      }
      // The drain ran to the end of the outbox AND the relay answered: any
      // earlier `401` is over — the login works again (R17). A drain that sent
      // nothing (an empty outbox) clears nothing, because it proved nothing; a
      // `410` discard or a rejected op still lands here, and should: neither is
      // an auth refusal.
      if (relayAnswered) {
        this.#authRequired = false;
        // R5.15: the same proof raises what the last failure lowered. Without
        // this the flag never rose in a live session — a restarted relay fires
        // no browser event, and `catchUp` only runs when a notebook opens.
        this.#goOnline();
      }
    } catch (e) {
      if (e instanceof RelayUnauthorizedError) {
        // 401: the login is missing or expired. The outbox is deliberately NOT
        // touched — it is the only copy of work authored offline, and the user
        // signing in again is what makes it sendable. Stopping the drain here
        // keeps the batch order (A11) and the retry path identical: the next
        // flush starts from the same first uncommitted op.
        //
        // Distinguished from the 410 branch above, which DOES discard: a
        // deleted notebook can never accept these ops, an expired login can.
        this.#authRequired = true;
        this.#emit();
        return { sent, committed, rejected };
      }
      // Network failure: the ops stay pending in ordinal order and go round
      // again on the next attempt. I6 — nothing is lost.
      //
      // R5.15 (user ruling 2026-10-16): an unreachable relay IS the offline
      // state — the flag is lowered here as well as in `catchUp`, because the
      // common failure is exactly this drain failing while the outbox fills.
      // (Retries are untouched: `scheduleFlush`/`flush` gate on the test hook
      // `#offline`, not this flag — the old worry that lowering it would stop
      // a client from ever retrying was about a race that never stops them.)
      this.#goOffline();
    } finally {
      this.#flushing = false;
    }

    await this.refresh();
    this.#emit();
    return { sent, committed, rejected };
  }

  /** Builds the committed envelope for a verdict from what the server returned. */
  async #committedFor(
    relay: Relay,
    response: { head_seq: number; server_time: number },
    verdict: Verdict,
    row: { envelope: SyncEnvelope },
  ): Promise<CommittedEnvelope | null> {
    // Prefer catching the record back from /log: it is the server's own
    // serialisation, including `child_assigned`, which the POST verdict only
    // summarises.
    const seq = verdict.seq as number;
    const page = await relay.fetchLog(seq - 1, 1).catch(() => null);
    if (page && page.ops.length > 0) return page.ops[0]!;

    // Fall back to synthesising from the verdict — enough to render, and always
    // superseded by the next catch-up.
    const assigned = (verdict.assigned ?? null) as CommittedEnvelope['assigned'];
    const childAssigned = (verdict.child_assigned ?? []) as CommittedEnvelope['child_assigned'];
    return {
      seq,
      server_time: response.server_time,
      op_id: row.envelope.op_id,
      owner_id: row.envelope.owner_id,
      device_id: row.envelope.device_id,
      notebook_id: row.envelope.notebook_id,
      op: row.envelope.op,
      assigned,
      child_assigned: childAssigned,
    };
  }

  /** Coalesces flush requests; the loop is serialised inside `flush`. */
  #flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Drains the outbox unconditionally, ignoring the offline flag.
   *
   * Separate from `flush` because `setOffline(true)` is how a test says "this
   * client must not reach the network on a timer" — not "this client can never
   * transmit". An explicit drain is the caller saying so.
   */
  async flushNow(relay?: Relay): Promise<FlushReport> {
    const wasOffline = this.#offline;
    this.#offline = false;
    try {
      return await this.flush(relay);
    } finally {
      this.#offline = wasOffline;
    }
  }

  /** Cancels a pending automatic flush, so a caller can drive the drain itself. */
  cancelScheduledFlush(): void {
    if (this.#flushTimer) {
      clearTimeout(this.#flushTimer);
      this.#flushTimer = null;
    }
  }

  scheduleFlush(delayMs = 50): void {
    if (this.#offline) return;
    if (this.#flushTimer) clearTimeout(this.#flushTimer);
    this.#flushTimer = setTimeout(() => {
      this.#flushTimer = null;
      void this.flush();
    }, delayMs);
  }

  // ── catch-up and downstream ────────────────────────────────────────────────

  /**
   * Catches up via `/log` BEFORE flushing the outbox (§8.2).
   *
   * Flushing first is legal but produces a larger relayout and a worse ordering
   * of the user's own work relative to their own devices, so the order is
   * deliberate: rebase onto the newest prefix, then transmit.
   */
  async catchUp(relay?: Relay): Promise<{ learned: number; relayout: RelayoutEntry[]; deleted: boolean }> {
    const relay2 = relay ?? this.#transport ?? defaultRelay(this.identity);
    const before = this.#model.committed;
    let since = before.head_seq;
    let freshTotal = 0;

    for (;;) {
      let page: LogPage;
      try {
        page = await relay2.fetchLog(since, 500);
      } catch (e) {
        if (e instanceof RelayGoneError) {
          // The notebook is gone on the relay. That means "deleted elsewhere"
          // only when this device holds history for it; a 404 with an empty
          // local log is a fresh, not-yet-created notebook.
          const ops = await committedOps(this.identity.notebook_id);
          const head = ops.reduce((max, env) => Math.max(max, env.seq), 0);
          return { learned: 0, relayout: [], deleted: head > 0 };
        }
        // R17 (Task 8): a `401` is not "unreachable". `catchUp` is the path that
        // runs BEFORE the flush on every notebook open (§8.2), so folding an
        // expired login into the same branch as a dead network would hide it:
        // the outbox would look stalled and nothing would ask the user to sign in
        // again. Same flag, same prompt as the flush path.
        //
        // Both fields this branch writes are render-relevant — the offline badge
        // reads `online`, the re-login prompt reads `authRequired` — so the
        // subscribers are told.
        if (e instanceof RelayUnauthorizedError) this.#authRequired = true;
        this.#goOffline();
        return { learned: 0, relayout: [], deleted: false };
      }

      this.#goOnline();
      if (page.ops.length === 0) break;

      // A PRUNED server log is unbridgeable: the served ops begin past our
      // head + 1, so no fetch can recover the missing middle, and folding the
      // tail over the nil shell dies (MalformedEnvelope) before the notebook
      // can open at all. The snapshot is the only base that reaches the served
      // tail (§11.1) — the flush resync's own bootstrap, on the read path.
      if (page.ops[0]!.seq > since + 1) {
        const projection = await relay2.fetchSnapshot();
        if (projection) {
          await this.seedFromSnapshot(projection);
          freshTotal += 1;
          since = projection.head_seq;
        }
        // Without a snapshot the server pruned more than it covers — its
        // invariant (§11.1), not ours. Fall through and let today's path
        // surface it.
      }

      // Drop anything at or below head_seq: idempotent by seq (§8.3).
      const fresh = page.ops.filter((e) => e.seq > before.head_seq);
      if (fresh.length > 0) {
        await storeCommitted(fresh, this.identity.device_id);
        await this.#ensureContext();
        freshTotal += fresh.length;
      }
      if (!page.has_more) break;
      // Paginate from the last op we just stored — the server's own floor may
      // sit BELOW that after a resync, and re-fetching from it would serve the
      // same page forever.
      since = page.ops[page.ops.length - 1]!.seq;
    }

    // The sync poll's no-op tick (R5.15): nothing new on the relay means no
    // IDB reads and no emit churn — the poller runs every few seconds, and a
    // quiet relay must stay quiet.
    if (freshTotal === 0) {
      return { learned: 0, relayout: [], deleted: false };
    }

    const after = await this.#currentProjection();
    const diff = relayoutBetween(before, after);
    await this.refresh();
    this.#emit();
    return { learned: Object.keys(after.tasks).length, relayout: diff, deleted: false };
  }

  /** Stores envelopes pushed over the WebSocket. */
  async receiveCommitted(envelopes: CommittedEnvelope[]): Promise<void> {
    const fresh = envelopes.filter(
      (e) => e.seq > this.#model.committed.head_seq,
    );
    if (fresh.length === 0) return; // idempotent by seq
    await storeCommitted(fresh, this.identity.device_id);
    await this.refresh();
    this.#emit();
  }

  // ── the session cell (Channel 2) ───────────────────────────────────────────

  /**
   * Reads the local mirror, creating it on first use (§5.5).
   *
   * The initial cell is PERSISTED, not just returned. Without that, every
   * call would mint a fresh cell stamped with the current time, and the
   * 4-hour idle check would restart from zero on each read — a session could
   * never time out at all.
   */
  async loadSession(): Promise<AutofocusSession | null> {
    const identity = this.identity;
    if (this.#stale()) return this.#session;
    const existing = await readSession(identity.notebook_id);
    if (existing) {
      this.#session = existing;
      return existing;
    }

    const initial: AutofocusSession = {
      notebook_id: identity.notebook_id,
      device_id: identity.device_id,
      lamport: 1,
      current_page_number: 1,
      current_task_id: null,
      items_worked_on_page: 0,
      cycle_boundary_page_number: null,
      status: 'Active',
      last_activity_at: Date.now(),
    };
    await writeSession(initial);
    this.#session = initial;
    return initial;
  }

  /**
   * Updates the local cell and persists it.
   *
   * `lamport` increments on every local write, which is what makes the LWW merge
   * deterministic without a round trip (§5.2).
   */
  async updateSession(patch: Partial<Omit<AutofocusSession, 'notebook_id' | 'device_id'>>): Promise<AutofocusSession> {
    const current = (await this.loadSession())!;
    const next: AutofocusSession = {
      ...current,
      ...patch,
      notebook_id: current.notebook_id,
      device_id: current.device_id,
      lamport: current.lamport + 1,
      last_activity_at: Date.now(),
    };
    if (this.#stale()) return next;
    await writeSession(next);
    this.#session = next;
    this.#emit();
    return next;
  }

  /**
   * A18: `items_worked_on_page` increments when FinishTask or ReenterTask is
   * authored for a task on the current page, and resets on entering a new page.
   *
   * This is the Guillotine disarm trigger — the whole dismissal rule reads it.
   */
  async bumpItemsWorked(): Promise<void> {
    const cell = await this.loadSession();
    if (!cell) return;
    await this.updateSession({ items_worked_on_page: cell.items_worked_on_page + 1 });
  }

  /**
   * Entering a page re-arms the guillotine for that visit.
   *
   * `items_worked_on_page` resets to 0 because "returning to a page re-arms it
   * (a fresh look is a fresh chance), while an interruption or break does not
   * reset the count" (ARCHITECTURE.md §1, rule 5). Page navigation is the only
   * thing that resets it.
   */
  async enterPage(pageNumber: number): Promise<AutofocusSession> {
    return this.updateSession({
      current_page_number: pageNumber,
      items_worked_on_page: 0,
    });
  }

  /** Merges an incoming cell from the relay by lexicographic max (§5.2). */
  async mergeIncomingSession(incoming: AutofocusSession): Promise<AutofocusSession> {
    const current = (await this.loadSession())!;
    const merged = mergeSession(current, incoming);
    if (this.#stale()) return merged;
    await writeSession(merged);
    this.#session = merged;
    this.#emit();
    return merged;
  }

  /**
   * Channel 2 publish: `updateSession` → `postSession` → merge the answer.
   *
   * The local write happens first and is what the caller gets back, because
   * focus entry must never block on the network: REPLICATION.md §8.1 is explicit
   * that the session cell "does not need durability". A failed publish leaves
   * the local cell correct — the worst case is that the other device reads a
   * slightly stale focus, which is the correct behaviour for an LWW register,
   * so the error is swallowed rather than surfaced.
   *
   * Transport is `POST .../session` rather than the WebSocket frame §5.2
   * specifies (D1). Both routes already exist on the relay; the browser had no
   * WebSocket client at all, so this is what actually makes the cell reachable.
   * Live cross-device focus mirroring is a future feature, not a gap.
   *
   * Callers deliberately do not await this — see Task 6's container wiring.
   */
  async publishSession(
    patch: Partial<Omit<AutofocusSession, 'notebook_id' | 'device_id'>>,
  ): Promise<AutofocusSession> {
    const local = await this.updateSession(patch);
    const relay = this.#transport ?? defaultRelay(this.identity);
    try {
      await this.mergeIncomingSession(await relay.postSession(local));
    } catch {
      // Deliberately silent. See the doc comment.
    }
    return local;
  }

  /**
   * Whether the 4-hour inactivity threshold has elapsed (§5.5).
   *
   * Strictly duration-based and deliberately blind to midnight boundaries, so a
   * late-night session crossing midnight is never aborted. A timeout authors NO
   * log operation — it is session status only (ARCHITECTURE.md §5).
   */
  async hasTimedOut(now: number = Date.now()): Promise<boolean> {
    const cell = await this.loadSession();
    if (!cell) return false;
    return now - cell.last_activity_at >= FOUR_HOURS_MILLIS;
  }

  // ── re-render ──────────────────────────────────────────────────────────────

  /** Recomputes both halves of the render model from durable state. */
  async refresh(): Promise<RenderModel> {
    const pending = await pendingOps(this.identity.notebook_id);
    await this.#ensureContext();

    const committed = await this.#currentProjection();
    const predicted = foldPending(committed, pending, this.#ctx!);

    this.#model = { committed, predicted };
    this.#emit();
    return this.#model;
  }

  /**
   * Seeds the local log from a snapshot.
   *
   * The snapshot IS a fold output, not an op list, so it cannot simply be
   * appended to `opLog`. It replaces the projection cache and is treated as the
   * new committed prefix; the ops after it arrive via `/log`.
   */
  async seedFromSnapshot(projection: Projection): Promise<void> {
    const database = await db();
    const tx = database.transaction(['notebooks', 'pages', 'tasks'], 'readwrite');
    await tx.objectStore('notebooks').clear();
    await tx.objectStore('pages').clear();
    await tx.objectStore('tasks').clear();

    await tx.objectStore('notebooks').put({
      ...projection.notebook,
      id: projection.notebook.id,
    });
    for (const page of Object.values(projection.pages)) {
      await tx.objectStore('pages').put(page);
    }
    for (const task of Object.values(projection.tasks)) {
      await tx.objectStore('tasks').put(task);
    }
    await tx.done;
    this.#snapshot = projection;
    // SeedFromSnapshot runs inside `catchUp`'s resync path, whose tail emits;
    // the transition guard keeps this from doubling anything.
    this.#goOnline();
  }

  /** Rewrites every pending envelope's advisory `base_seq` to the new head. */
  async restampOutboxBaseSeq(headSeq: number): Promise<void> {
    const database = await db();
    const tx = database.transaction('opLog', 'readwrite');
    let cursor = await tx.store.index('byOrdinal').openCursor();
    while (cursor) {
      if (cursor.value.seq === null) {
        cursor.value.envelope = { ...cursor.value.envelope, base_seq: headSeq };
        await cursor.update(cursor.value);
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  }

  /**
   * The committed projection, from whichever prefix the client actually holds.
   *
   * Normally that is the whole log and `fold` is the definition. After a resync
   * it is `snapshot + tail` (§11.1) — the snapshot is `fold` output at its
   * `covered_through_seq`, so the result is identical to a full replay.
   */
  async #currentProjection(): Promise<Projection> {
    const ops = await committedOps(this.identity.notebook_id);
    if (this.#snapshot) {
      // The snapshot already CONTAINS every op at or below its head_seq, so
      // replaying them would re-apply `CreateNotebook` against a projection that
      // already has a page — which the engine rightly refuses. Only the tail
      // beyond the snapshot is new (§11.1).
      const tail = ops.filter((e) => e.seq > this.#snapshot!.head_seq);
      return foldFromSnapshot(this.#snapshot, tail, this.#ctx!);
    }
    return fold(ops, this.#ctx!);
  }

  async #ensureContext(): Promise<void> {
    if (this.#ctx) return;
    // The derived window is kept alongside the context because callers — the
    // page view, the dismissal action — need page ids without an await.
    this.#pageIds = await derivePageIds(this.identity.notebook_id, PAGE_WINDOW);
    this.#ctx = { pageIds: this.#pageIds };
  }

  /** The outbox, for tests and the debug view. */
  async outbox(): Promise<SyncEnvelope[]> {
    return pendingOps(this.identity.notebook_id);
  }

  /** The id of page `n`, derived rather than read (A1, §3.1). */
  pageId(pageNumber: number): string {
    if (!this.#pageIds) throw new Error('store not bootstrapped');
    const id = this.#pageIds.get(pageNumber);
    if (!id) throw new Error(`page ${pageNumber} is beyond the derived window`);
    return id;
  }

  /** Committed envelopes, ascending by seq. */
  async committed(): Promise<CommittedEnvelope[]> {
    return committedOps(this.identity.notebook_id);
  }

  /** Direct database handle, for tests that need to inspect raw rows. */
  async raw(): Promise<ReturnType<typeof db> extends Promise<infer T> ? T : never> {
    return db();
  }
}

// ── relay port ────────────────────────────────────────────────────────────────

/**
 * The relay says the notebook is gone: HTTP 410 on a batch write, HTTP 404 on
 * a log read for an id the relay has purged (R3.2 §1). Distinct from a generic
 * network failure — the caller's drop rules, not the retry path, own it.
 */
export class RelayGoneError extends Error {}

/**
 * The relay says the request is unauthenticated: HTTP 401 from any route while
 * the relay is in account mode and the browser's login is missing or expired.
 *
 * The opposite signal to `RelayGoneError`, and the distinction is load-bearing:
 * a gone notebook discards its outbox, an expired login must not. Every pending
 * op is offline-authored work and is kept until the user signs in again.
 */
export class RelayUnauthorizedError extends Error {}

/**
 * Permanently deletes a notebook on the relay (R3.2 §1): purge + audit row.
 *
 * A 404 is success (already gone — delete → delete); anything else non-2xx
 * throws on the existing generic path.
 */
export async function deleteNotebook(notebookId: string): Promise<void> {
  const deviceId = await getOrCreateDeviceId();
  const response = await fetch(
    `/api/owners/${currentDbOwner()}/notebooks/${notebookId}?device_id=${deviceId}`,
    { method: 'DELETE' },
  );
  if (response.status === 404) return;
  if (!response.ok) throw new Error(`delete: ${response.status}`);
}

/** One verdict from a batch (§8.2). */
export type Verdict = Record<string, unknown> & { op_id?: string | null };

/** The server's answer to a batch: per-op verdicts, or a resync instruction (§9.3). */
export type BatchResponse =
  | { results: Verdict[]; head_seq: number; server_time: number }
  | { resync_required: true; head_seq: number; snapshot_url: string };

/**
 * The transport contract, kept narrow so tests can substitute a fake and the
 * store never learns whether it is talking to a real relay (§8.1: operations go
 * UP over POST and DOWN over WebSocket).
 */
export interface Relay {
  postOps(ops: SyncEnvelope[]): Promise<BatchResponse>;
  fetchLog(since: number, limit: number): Promise<LogPage>;
  fetchSnapshot(): Promise<Projection | null>;
  /** Channel 2. Returns the cell the server settled on after LWW merge (D1). */
  postSession(cell: AutofocusSession): Promise<AutofocusSession>;
  /** The owner-scoped notebook list (Step 7, §D7/D9). */
  listNotebooks(): Promise<{ notebooks: NotebookSummary[] }>;
}

/** How many ops go up in one batch. */
const BATCH_SIZE = 50;

/**
 * R5.15 (2026-10-16): the store polls `GET /log` on this cadence, forever.
 * While the flag is DOWN it is the recovery probe (a restarted relay fires no
 * browser event, and with an empty outbox nothing else would contact it);
 * while HEALTHY it is the cross-browser sync — two online browsers pull each
 * other's ops without any local action. No-op ticks (nothing new on the
 * relay) cost one `GET` and no emit. Tests inject a short interval so a poll
 * lands in milliseconds on real timers.
 */
const SYNC_POLL_MS = 5_000;
/** The 4-hour inactivity threshold, in milliseconds. */
export const FOUR_HOURS_MILLIS = 4 * 60 * 60 * 1000;

/**
 * A relay over `fetch`, used when no transport is injected.
 *
 * `origin` prefixes every URL (Step 8, D6): the default empty string keeps
 * same-origin relative paths — what the Vite dev proxy exists for — and the
 * e2e passes an absolute `http://127.0.0.1:PORT` to talk to a real relay.
 *
 * Every method maps `401` to `RelayUnauthorizedError` (Task 9): in account mode
 * the auth gate answers it for any route when the browser's login is missing or
 * expired, and the caller must be able to tell that apart from a 410/404
 * "notebook gone" — one pauses, the other discards.
 *
 * The owner segment is `currentDbOwner`, not `identity.owner_id` (design §5):
 * the gate binds the path's owner to the LOGIN, so the path must name the
 * account this browser is acting as. `identity` still supplies the notebook, and
 * a store built by the gate carries the acting owner as its identity anyway —
 * the two agree by construction, and where they could not, the login wins.
 */
export function defaultRelay(identity: Identity, origin = ''): Relay {
  const ownerId = currentDbOwner();
  const base = `${origin}/api/owners/${ownerId}/notebooks/${identity.notebook_id}`;

  return {
    async postOps(ops) {
      const response = await fetch(`${base}/ops`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ops }),
      });
      if (response.status === 401) throw new RelayUnauthorizedError(`ops: ${response.status}`);
      if (response.status === 410) throw new RelayGoneError(`ops: ${response.status}`);
      if (!response.ok) throw new Error(`ops: ${response.status}`);
      const body = (await response.json()) as Record<string, unknown>;
      if (body.resync_required) return body as BatchResponse;
      return body as BatchResponse;
    },
    async fetchLog(since, limit) {
      const response = await fetch(`${base}/log?since=${since}&limit=${limit}`);
      if (response.status === 401) throw new RelayUnauthorizedError(`log: ${response.status}`);
      if (response.status === 404) throw new RelayGoneError(`log: ${response.status}`);
      if (!response.ok) throw new Error(`log: ${response.status}`);
      return (await response.json()) as LogPage;
    },
    async fetchSnapshot() {
      const response = await fetch(`${base}/snapshot?at=0`);
      if (response.status === 204) return null;
      if (response.status === 401) throw new RelayUnauthorizedError(`snapshot: ${response.status}`);
      if (!response.ok) throw new Error(`snapshot: ${response.status}`);
      const body = (await response.json()) as { projection: Projection };
      return body.projection;
    },
    async postSession(cell) {
      const response = await fetch(`${base}/session`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(cell),
      });
      if (response.status === 401) throw new RelayUnauthorizedError(`session: ${response.status}`);
      if (!response.ok) throw new Error(`session: ${response.status}`);
      return (await response.json()) as AutofocusSession;
    },
    async listNotebooks() {
      const response = await fetch(`${origin}/api/owners/${ownerId}/notebooks`);
      if (response.status === 401) throw new RelayUnauthorizedError(`notebooks: ${response.status}`);
      if (!response.ok) throw new Error(`notebooks: ${response.status}`);
      return (await response.json()) as { notebooks: NotebookSummary[] };
    },
  };
}

/** The browser's IANA zone name, used at notebook creation (§7.2a). */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Result of one drain. */
export interface FlushReport {
  sent: number;
  committed: number;
  rejected: number;
}

/**
 * The same outbox row, re-addressed to `ownerId`.
 *
 * Every owner-bearing field moves together: the envelope's `owner_id` (baked in
 * at `authorise`, and what the relay's gate checks) and, when a row still carries
 * one, the committed record's. `op_id`, `device_id`, `localOrdinal` and the op
 * itself are untouched — this is an address change, not a re-authoring.
 */
function restampRowOwner(row: OpRow, ownerId: string): OpRow {
  return {
    ...row,
    envelope: { ...row.envelope, owner_id: ownerId },
    committed: row.committed ? { ...row.committed, owner_id: ownerId } : null,
  };
}

function emptyProjectionSafe(): Projection {
  return {
    notebook_id: '00000000-0000-0000-0000-000000000000',
    notebook: {
      id: '00000000-0000-0000-0000-000000000000',
      owner_id: '00000000-0000-0000-0000-000000000000',
      name: '',
      tag: '',
      description: '',
      page_capacity: 25,
      timezone: 'UTC',
    },
    head_seq: 0,
    tasks: {},
    pages: {},
  };
}

function relayoutBetween(
  previous: Projection,
  next: Projection,
): RelayoutEntry[] {
  const entries: RelayoutEntry[] = [];
  for (const [taskId, nextTask] of Object.entries(next.tasks)) {
    const before = previous.tasks[taskId];
    if (!before) continue;
    if (before.page_id === nextTask.page_id && before.line_index === nextTask.line_index) {
      continue;
    }
    entries.push({
      task_id: taskId,
      old_page_id: before.page_id,
      old_line_index: before.line_index,
      new_page_id: nextTask.page_id,
      new_line_index: nextTask.line_index,
    });
  }
  return entries;
}